import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Home, DollarSign, Clock, Info } from 'lucide-react';
import { pmsFrom } from '@/integrations/pms';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { ConfigurableLayout } from '@/components/common/ConfigurableLayout';
import { PageSidebar } from '@/components/common/PageSidebar';
import { SEOHead } from '@/components/seo/SEOHead';
import { FAQJsonLd } from '@/components/seo/JsonLd';
import { FAQ } from '@/components/common/FAQ';
import { LeadForm } from '@/components/forms/LeadForm';
import { usePageDesign } from '@/hooks/usePageDesign';
import { sanitizeHtml } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';

const ServiceDetailPage: React.FC = () => {
  const params = useParams<{ segSlug?: string; catSlug?: string; serviceSlug?: string; a?: string; b?: string; c?: string }>();
  const segSlug = params.segSlug || params.a;
  const catSlug = params.catSlug || params.b;
  const serviceSlug = params.serviceSlug || params.c;

  const { data: service, isLoading } = useQuery({
    queryKey: ['service-detail', serviceSlug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('services')
        .select('*, service_categories!inner(name, slug, segment_id, service_segments!inner(name, slug))')
        .eq('slug', serviceSlug!)
        .eq('is_active', true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!serviceSlug,
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-public'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings').select('company_name, phone, industry').limit(1).maybeSingle();
      return data;
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';
  const phone = businessSettings?.phone || '';

  // Pricing intelligence — check if pricing display is enabled
  const { data: pricingConfig } = useQuery({
    queryKey: ['pricing-intelligence-config'],
    queryFn: async () => {
      const { data } = await pmsFrom('pricing_config')
        .select('is_enabled')
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const pricingEnabled = (pricingConfig as any)?.is_enabled ?? false;

  // Service pricing — only fetch when pricing is enabled and we have a service
  const { data: servicePricing } = useQuery({
    queryKey: ['service-pricing', service?.id],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_pricing')
        .select('*')
        .eq('service_id', service!.id)
        .eq('is_active', true)
        .order('service_name');
      if (error) throw error;
      return data;
    },
    enabled: pricingEnabled && !!service?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Page design config
  const { heroStyle, layoutVariant, showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection, showLeadForm, isLoading: designLoading } = usePageDesign('service');

  if (isLoading || designLoading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-24 space-y-6">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!service) return null;

  const faq = Array.isArray(service.faq) ? service.faq as Array<{ question: string; answer: string }> : [];
  const category = (service as any).service_categories;
  const segment = category?.service_segments;

  // Build breadcrumbs
  const breadcrumbs = [
    { label: 'Home', href: '/', icon: <Home className="h-4 w-4" /> },
    { label: 'Services', href: '/services' },
    ...(segment ? [{ label: segment.name, href: `/${segment.slug}` }] : []),
    ...(category ? [{ label: category.name, href: `/${segment?.slug}/${category.slug}` }] : []),
    { label: service.name },
  ];

  // Main content
  const mainContent = (
    <>
      {service.featured_image && (
        <img
          src={service.featured_image}
          alt={service.featured_image_alt || service.name}
          className="w-full rounded-xl object-cover max-h-96"
          loading="lazy"
        />
      )}

      {service.content && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg max-w-none dark:prose-invert seo-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.content) }}
        />
      )}

      {/* Pricing ranges (shown only when pricing_enabled and data exists) */}
      {pricingEnabled && servicePricing && servicePricing.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            Pricing Estimates
          </h2>
          <div className="space-y-3">
            {servicePricing.map((p) => (
              <div key={p.id} className="bg-card rounded-xl border border-border p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-base">{p.service_name}</h3>
                  {p.notes && <p className="text-sm text-muted-foreground mt-1">{p.notes}</p>}
                </div>
                <div className="flex items-center gap-4 text-right">
                  {(p.price_range_low != null || p.price_range_high != null) && (
                    <div>
                      <p className="text-lg font-bold text-primary">
                        {p.price_range_low != null && p.price_range_high != null
                          ? `$${p.price_range_low.toLocaleString()} – $${p.price_range_high.toLocaleString()}`
                          : p.price_range_low != null
                          ? `Starting at $${p.price_range_low.toLocaleString()}`
                          : `Up to $${p.price_range_high!.toLocaleString()}`}
                      </p>
                      {p.emergency_multiplier && p.emergency_multiplier > 1 && (
                        <p className="text-xs text-muted-foreground">Emergency: {p.emergency_multiplier}x</p>
                      )}
                    </div>
                  )}
                  {p.duration_estimate && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {p.duration_estimate}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 mt-4 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Prices are estimates and may vary based on scope, equipment, and site conditions. Contact us for an accurate quote.</span>
          </div>
        </motion.section>
      )}

      {showFaqSection && faq.length > 0 && (
        <section>
          <h2 className="font-heading text-2xl font-bold mb-6">Frequently Asked Questions</h2>
          <FAQ faqs={faq} />
        </section>
      )}

      {showLeadForm && (
        <section className="bg-card rounded-xl border border-border p-8">
          <h2 className="font-heading text-2xl font-bold mb-2">Get a Free {service.name} Estimate</h2>
          <p className="text-muted-foreground mb-6">Fill out the form below and we'll get back to you promptly.</p>
          <LeadForm source={`service-${service.slug}`} />
        </section>
      )}
    </>
  );

  // Sidebar content
  const sidebarContent = (
    <PageSidebar
      showCta={showCtaButtons}
      showTrustBadges={showTrustBadges}
      showPromo
      showServiceAreas
      showRecentPosts
      showRelatedServices
      entityContext={{
        type: 'service',
        id: service.id,
        categoryId: service.category_id,
        slug: service.slug,
      }}
      ctaTitle={`Need ${service.name}?`}
      ctaDescription={`Contact ${companyName} for expert ${service.name} services.`}
    />
  );

  return (
    <>
      <SEOHead
        title={service.meta_title || `${service.name} | ${companyName}`}
        description={service.meta_description || service.description || `Professional ${service.name} services by ${companyName}.`}
        ogImage={service.og_image || service.featured_image || undefined}
      />
      {faq.length > 0 && <FAQJsonLd faqs={faq} />}

      <ConfigurableHero
        heroStyle={heroStyle}
        title={service.name}
        subtitle={service.description || `Professional ${service.name} services`}
        badge={category?.name || 'Services'}
        backgroundImage={service.featured_image || undefined}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={showBreadcrumbs}
        showTrustBadges={false}
        showCtaButtons={showCtaButtons}
        showRating
        phone={phone}
      />

      <ConfigurableLayout
        layout={layoutVariant}
        main={mainContent}
        sidebar={sidebarContent}
        className="pb-24"
      />
    </>
  );
};

export default ServiceDetailPage;
